// src/components/Performance/TimelineView.tsx

import React, { useState } from "react";
import { Calendar } from "lucide-react";
import { Empty, Tooltip } from "antd";
import { feedbackConfig, mapTypeToConfigKey } from "./performanceHelper";
import ModalLoader from "../../../../../../components/Loader/ModalLoader";
import DeletePerformanceNoteModal from "./DeletePerformanceNoteModal";
import AddNoteModal from "./AddNoteModal";

interface PerformanceNote {
  reviewStatus: string;
  employeePerformanceNotesId: string;
  employeeId: string;
  type: string;
  title: string;
  description: string;
  nextSteps?: string;
  createdOn?: string;
  adminName: string;
  adminRole: string;
}

interface TimelineViewProps {
  performanceData?: PerformanceNote[];
  loading: boolean;
  refreshData: () => void;
  pastEmployee: boolean;
}

const TimelineView: React.FC<TimelineViewProps> = ({ performanceData, loading, refreshData, pastEmployee }) => {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState<{ employeeId: string; noteId: string } | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingNote, setEditingNote] = useState<PerformanceNote | null>(null);

  const openDeleteModal = (noteId: string, employeeId: string) => {
    setSelectedNote({ noteId, employeeId });
    setDeleteModalOpen(true);
  };

  const openEditModal = (note: PerformanceNote) => {
    setEditingNote(note);
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditingNote(null);
  };

  if (!performanceData || performanceData.length === 0) {
    return <div className="timeline-empty-wrapper flex justify-center items-center">{loading ? <ModalLoader /> : <Empty description="No performance notes found." />}</div>;
  }

  return (
    <div>
      {loading && (
        <div className="absolute inset-0 bg-white bg-opacity-70 flex items-center justify-center z-10">
          <ModalLoader />
        </div>
      )}

      <div className="relative  pr-4 flex-1  ">
        <div className="relative">
          <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-[#d34a7c]/30 "></div>

          {performanceData.map((note) => {
            const configKey = mapTypeToConfigKey(note.type || "");
            const config = feedbackConfig[configKey];
            const Icon = config.icon;

            return (
              <div key={note.employeePerformanceNotesId} className="relative flex items-start space-x-2 pb-8 ">
                <div className={`flex-shrink-0 w-12 h-12 rounded-full border-4 border-white shadow-sm flex items-center justify-center ${config.color}`}>
                  <Icon className="w-5 h-5" />
                </div>

                <div className="flex-1 bg-white rounded-lg border border-gray-200 shadow-sm ">
                  <div className="p-5 pb-2 flex items-center justify-between">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold leading-tight ${config.badgeColor}`}>{config.label}</span>

                    <div className="flex items-center gap-4 text-sm text-[#595959]/70">
                      {note.createdOn && (
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4 mb-1" />
                          <span>
                            {new Date(note.createdOn).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            })}
                          </span>
                        </div>
                      )}

                      {!pastEmployee && (
                        <div className="flex items-center gap-1 w-[50px] justify-end">
                          {note.reviewStatus === "Draft" && (
                            <Tooltip placement="top" title="Edit">
                              <span className="material-symbols-outlined cursor-pointer hover:text-primary" onClick={() => openEditModal(note)}>
                                edit
                              </span>
                            </Tooltip>
                          )}

                          <Tooltip placement="top" title="Delete">
                            <span
                              className={`material-symbols-outlined cursor-pointer hover:text-primary ${note.reviewStatus !== "Draft" ? "mx-auto" : ""}`}
                              onClick={() => openDeleteModal(note.employeePerformanceNotesId, note.employeeId)}
                            >
                              delete
                            </span>
                          </Tooltip>
                        </div>
                      )}
                    </div>
                  </div>

                  <h3 className="text-lg font-semibold text-[#595959] mt-2 px-6">{note.title}</h3>
                  <p className="text-base text-[#595959] leading-relaxed whitespace-pre-line mb-4 px-6">{note.description}</p>

                  {note.nextSteps && (
                    <div className="bg-[#d34a7c]/10 border-l-4 border-[#d34a7c] p-4 mb-4 rounded-r mx-6">
                      <div className="flex items-start space-x-3">
                        <div>
                          <p className="text-sm font-semibold text-[#d34a7c] mb-1">Next Steps:</p>
                          <p className="text-sm text-[#595959] leading-relaxed whitespace-pre-line">{note.nextSteps}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center text-sm text-[#595959]/70 px-6 pb-6 space-x-3">
                    <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-[10px] font-semibold text-[#595959] select-none">
                      {note.adminName
                        ?.split(" ")
                        .map((n) => n[0])
                        .join("")}
                    </div>
                    <span>
                      Added by {note.adminName} ({note.adminRole})
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {selectedNote && (
        <DeletePerformanceNoteModal open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} employeeId={selectedNote.employeeId} noteId={selectedNote.noteId} refreshData={refreshData} />
      )}

      <AddNoteModal
        open={editModalOpen}
        onCancel={closeEditModal}
        employeeId={Number(editingNote?.employeeId)}
        isEdit={true}
        noteData={editingNote}
        refreshData={() => {
          refreshData();
          closeEditModal();
        }}
      />
    </div>
  );
};

export default TimelineView;
